import { AccountInfo } from "01Data/01Admin/AccountControl";
import { AppContext } from "01Data/AppData";
import PageTemplate from "03AdminPages/00Templates/PageTemplate";
import { ConfirmDialog } from "04UserPages/99Parts/ConfirmDialog";
import {
  BuildingSection,
  DataDetailDlg,
  DetailForm,
  Pico3Section,
  SensorSection,
} from "97Parts/Andon/AndonSections";
import { LogIDSection } from "97Parts/Andon/LogSection";
import TabBar from "97Parts/Andon/TabBar";
import { NoteText } from "97Parts/Styles";
import { useModel, useSignal } from "99Tools/useModel";
import { ArrowCircleLeft, Delete } from "@mui/icons-material";
import { Backdrop, Button, CircularProgress, styled } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { LIST_HEADER_COLOR } from "Settings";
import { useContext, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: LIST_HEADER_COLOR,
  color: theme.palette.common.white,
}));

const ButtonDlg = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

/**
 * カラム設定
 */
const columns = [
  {
    id: "id",
    label: "アカウント",
    minWidthPC: 150,
    minWidthSmartphone: "15vw",
  },
  // { id: "pico3", label: "Pico3", minWidthPC: 150, minWidthSmartphone: "15vw" },
  {
    id: "sensor",
    label: "漏水みはり番",
    minWidthPC: 150,
    minWidthSmartphone: "15vw",
  },
  { id: "delete", label: "", minWidthPC: 120, minWidthSmartphone: "8vw" },
];

/**
 * アカウント一覧
 * @returns TSX
 */
const UserBody = () => {
  const admin = useContext(AppContext);
  const a = admin.dataAdmin.accounts;
  const andon = admin.dataAdmin.andon;

  const handleOpenTop = (i: number) => {
    a.andonAccount = a.users[i].user.UserName;
    a.andonID = a.users[i].user.Email;
    andon.UserId = a.users[i].user.UserId;
    a.changed();
  };
  const handleDelete = () => {
    a.DeleteUser(a.deleteAccount)
      .then(() => {
        a.GetUsers()
          .then(() => {
            a.bShowDeleteConfirmDlg = false;
            a.changed();
          })
          .catch((err: any) => {
            a.SetError(err, "アカウント削除失敗");
            a.bShowDeleteConfirmDlg = false;
            a.changed();
          });
      })
      .catch((err: any) => {
        a.SetError(err, "アカウント情報取得失敗");
        a.bShowDeleteConfirmDlg = false;
        a.changed();
      });
  };
  const handleConfirmDelete = (i: number) => {
    a.bShowDeleteConfirmDlg = true;
    a.deleteAccount = `${a.users[i].user.UserId}`;
    a.deleteAccountName = `${a.users[i].user.UserName}`;
    a.changed();
  };
  const handleCloseConfirmDlg = () => {
    a.bShowDeleteConfirmDlg = false;
    a.changed();
  };
  const rows = a.users
    .sort((a, b) => a.user.UserName.localeCompare(b.user.UserName))
    .slice(a.page * a.rowsPerPage, a.page * a.rowsPerPage + a.rowsPerPage)
    .map((row: AccountInfo, i: number) => {
      const index = a.page * a.rowsPerPage + i;
      return (
        <TableRow hover role="checkbox" tabIndex={-1} key={row.user.UserId}>
          <TableCell
            key={columns[0].id + i}
            align="center"
            sx={{ cursor: "pointer" }}
            onClick={() => handleOpenTop(index)}
          >
            {row.user.UserName}
            <br />({row.user.Email})
          </TableCell>
          {/* <TableCell
            key={columns[1].id + i}
            align="center"
            sx={{ cursor: "pointer" }}
            onClick={() => handleOpenTop(index)}
          >
            {row.GetPico3()}
          </TableCell> */}
          <TableCell
            key={columns[1].id + i}
            align="center"
            sx={{ cursor: "pointer" }}
            onClick={() => handleOpenTop(index)}
          >
            {row.GetSensors()}
          </TableCell>
          <TableCell key={columns[2].id + i} align="center">
            <Button
              startIcon={<Delete />}
              size="small"
              variant="contained"
              onClick={() => handleConfirmDelete(index)}
            >
              削除
            </Button>
          </TableCell>
        </TableRow>
      );
    });

  return (
    <>
      <TableBody>{rows}</TableBody>
      <ConfirmDialog
        bShow={a.bShowDeleteConfirmDlg}
        title={`${a.deleteAccountName}を削除しますか？`}
        description=""
        onApply={handleDelete}
        onCancel={handleCloseConfirmDlg}
        titleButtonApply="削除"
        titleButtonCancel="キャンセル"
      />
    </>
  );
};

/**
 * ユーザテーブル
 * @returns TSX
 */
const UserTable = () => {
  const a = useContext(AppContext).dataAdmin.accounts;

  const isSmartphone = useMediaQuery({
    query: "(max-width: 600px)",
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    a.page = newPage;
    a.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    a.rowsPerPage = +event.target.value;
    a.page = 0;
    a.changed();
  };

  const header = columns.map((column) => (
    <ColorHeader
      key={column.id}
      align="center"
      style={{
        minWidth: isSmartphone ? column.minWidthSmartphone : column.minWidthPC,
      }}
    >
      {column.label}
    </ColorHeader>
  ));

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ minHeight: "60vh" }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{header}</TableRow>
          </TableHead>
          <UserBody />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={a.users.length}
        rowsPerPage={a.rowsPerPage}
        page={a.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

const AndonPage = () => {
  const d = useContext(AppContext).dataAdmin;
  useModel(d.andon);
  const [bShowDlg, setShowDlg] = useState(false);
  const [dlgData, setDlgData] = useState(new DataDetailDlg());
  const [loading, setLoading] = useState(false);
  const buildingsRedraw = useSignal();
  const sensorsRedraw = useSignal();
  const pico3Redraw = useSignal();

  useEffect(() => {
    d.state = "andonUpdate";
    setLoading(true);
    d.andon
      .Update()
      .catch((err: any) => {
        d.andon.SetError(err, "データ取得に失敗しました。");
      })
      .finally(() => setLoading(false));
    return () => {
      d.state = "normal";
    };
  }, [d]);

  return (
    <>
      <BuildingSection
        setShowDlg={setShowDlg}
        setDlgData={setDlgData}
        redraw={buildingsRedraw}
        sensorsRedraw={sensorsRedraw}
        pico3Redraw={pico3Redraw}
      />
      <SensorSection
        setShowDlg={setShowDlg}
        setDlgData={setDlgData}
        redraw={sensorsRedraw}
      />
      <Pico3Section
        setShowDlg={setShowDlg}
        setDlgData={setDlgData}
        redraw={pico3Redraw}
      />
      <DetailForm
        bShowDlg={bShowDlg}
        dlgData={dlgData}
        setShowDlg={setShowDlg}
        buildingsRedraw={buildingsRedraw}
      />
      <Backdrop open={loading} sx={{ zIndex: 1201 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Backdrop>
    </>
  );
};

/**
 * ログ一覧表示
 * @returns TSX
 */
const LogIDPage = () => {
  const a = useContext(AppContext).dataAdmin.andon;
  useModel(a.log);
  return <LogIDSection log={a.log} devices={a.devices} />;
};

const UserMainPage = () => {
  const a = useContext(AppContext).dataAdmin;
  const accounts = a.accounts;
  const andon = a.andon;
  const handleReturn = () => {
    accounts.andonAccount = null;
    accounts.andonID = null;
    andon.Clear();
    accounts.changed();
  };

  const tabLabels: string[] = ["ステータス表示", "ログ表示"];
  const components = [<AndonPage key="andon" />, <LogIDPage key="log" />];

  return (
    <>
      <ButtonDlg
        size="small"
        startIcon={<ArrowCircleLeft />}
        variant="contained"
        color="primary"
        onClick={handleReturn}
      >
        戻る
      </ButtonDlg>
      <TabBar tabLabels={tabLabels}>{components}</TabBar>
    </>
  );
};

/**
 * アカウント一覧表示
 * @returns TSX
 */
const AccountsPage = () => {
  const a = useContext(AppContext).dataAdmin.accounts;
  useModel(a);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    a.page = 0;
    a.GetUsers()
      .then(() => {
        a.changed();
      })
      .catch((err: any) => {
        a.SetError(err, "アカウント情報取得失敗");
      })
      .finally(() => setLoading(false));
  }, [a]);

  // アカウント一覧
  if (!a.andonAccount) {
    return (
      <PageTemplate title="アカウント一覧">
        <UserTable />
        <Backdrop open={loading} sx={{ zIndex: 1201 }}>
          <CircularProgress sx={{ color: "white" }} />
        </Backdrop>
      </PageTemplate>
    );
  }

  // アンドン
  return (
    <PageTemplate title={`${a.andonAccount}`}>
      <NoteText>{a.andonID}</NoteText>
      <UserMainPage />
    </PageTemplate>
  );
};

export default AccountsPage;
